import React, { useState } from "react";
import {
  Grow,
  Icon,
  Paper,
  Button,
  Typography,
  ButtonGroup,
  ClickAwayListener,
  PopperPlacementType,
  Popper,
} from "@mui/material";
import { Editor } from "slate";
import { useSlate } from "slate-react";
import { Add, Remove } from "@mui/icons-material";

import { classes } from "../../styles";
import { CustomEditor } from "../../custom-types";

interface ISizeProps {
  id: number;
  value: string;
  label: string;
}

const sizeOptions: ISizeProps[] = [
  { id: 1, value: "xx-small", label: "XX Pequeno" },
  { id: 2, value: "x-small", label: "X Pequeno" },
  { id: 4, value: "small", label: "Pequeno" },
  { id: 5, value: "inherit", label: "Normal" },
  { id: 6, value: "medium", label: "Médio" },
  { id: 7, value: "large", label: "Grande" },
  { id: 8, value: "x-large", label: "X Grande" },
  { id: 9, value: "xx-large", label: "XX Grande" },
  { id: 10, value: "xxx-large", label: "XXX Grande" },
];

interface IProps {
  disabled?: boolean;
}

export const SizeButton: React.FC<IProps> = ({ disabled = false }) => {
  const editor = useSlate();

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [placement, setPlacement] = useState<PopperPlacementType>();

  const [currentFontSize, setCurrentFontSize] = useState<ISizeProps>();

  const isFontSizeActive = (editor: CustomEditor) => {
    const marks = Editor.marks(editor);

    if (marks) {
      return marks["fontSize"] ? marks["fontSize"] : false;
    }
  };

  const getCurrentFontSize = (editor: CustomEditor) => {
    const currentSize = isFontSizeActive(editor);

    const foundFontSize = sizeOptions.find((option) =>
      currentSize ? option.value === currentSize : option.value === "inherit"
    );

    return foundFontSize;
  };

  const handleMarkButton = (editor: CustomEditor, option: string) => {
    const currentSize = isFontSizeActive(editor);

    const foundIndex = sizeOptions.findIndex((option) =>
      currentSize ? option.value === currentSize : option.value === "inherit"
    );

    if (option === "up" && sizeOptions[foundIndex + 1]) {
      if (sizeOptions[foundIndex + 1].value !== "inherit") {
        Editor.addMark(editor, "fontSize", sizeOptions[foundIndex + 1].value);
      } else {
        Editor.removeMark(editor, "fontSize");
      }
    } else if (option === "down" && sizeOptions[foundIndex - 1]) {
      if (sizeOptions[foundIndex - 1].value !== "inherit") {
        Editor.addMark(editor, "fontSize", sizeOptions[foundIndex - 1].value);
      } else {
        Editor.removeMark(editor, "fontSize");
      }
    }

    setCurrentFontSize(getCurrentFontSize(editor));
  };

  const handleToggle =
    (newPlacement: PopperPlacementType) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      setOpen((prev) => placement !== newPlacement || !prev);
      setPlacement(newPlacement);
      setCurrentFontSize(getCurrentFontSize(editor));
    };

  const handleClose = () => setOpen(false);

  return (
    <>
      <Button
        size="small"
        disabled={disabled}
        variant="contained"
        disableElevation={!isFontSizeActive(editor)}
        color={!isFontSizeActive(editor) ? "primary" : "secondary"}
        sx={classes.button}
        onClick={handleToggle("bottom-start")}
      >
        <Icon fontSize="small" sx={classes.icon}>
          format_size
        </Icon>
      </Button>
      <Popper
        style={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorEl}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <ButtonGroup variant="text" color="primary">
                  <Button
                    aria-label="botão aumentar fonte"
                    onClick={() => handleMarkButton(editor, "up")}
                  >
                    <Add fontSize="small" />
                  </Button>
                  <Button
                    aria-label="botão diminuir fonte"
                    onClick={() => handleMarkButton(editor, "down")}
                  >
                    <Remove fontSize="small" />
                  </Button>
                  <Button aria-label="fonte atual" disabled>
                    <Typography color="primary" variant="caption">
                      {currentFontSize && currentFontSize.label}
                    </Typography>
                  </Button>
                </ButtonGroup>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
